'use client'

import { useQuery } from '@tanstack/react-query'
import { useLocale, useTranslations } from 'next-intl'
import { POPUP_TRIGGER_HEIGHT } from 'constant'
import useScrollingCallback from 'hook/useScrollingCallback'
import { fetchExplores } from 'server-actions/fetch-search'
import { usePopupStore } from 'store/global/popup'
import { useRefreshSearchPageStore } from 'store/global/refresh-page'
import { useDeviceStore } from 'store/server-context/device'
import { useIpLocationStore } from 'store/server-context/ip-location'
import { useSessionStore } from 'store/server-context/session'
import Link from 'next/link'
import CommunitiesList from './list/communities-list'
import VideoSpotlights from './video-spotlights'

const SearchExplores = () => {
  const locale = useLocale()
  const t = useTranslations()
  const ipLocation = useIpLocationStore(state => state.ipLocation)
  const refreshingId = useRefreshSearchPageStore(state => state.refreshingId)
  const isOpen = usePopupStore(state => state.mask.download)
  const showPopup = usePopupStore(state => state.showDownloadPopup)
  const device = useDeviceStore(state => state.device)
  const session = useSessionStore(state => state.session)
  const SIZE = 5

  const { data, isLoading } = useQuery({
    queryKey: ['search', refreshingId],
    queryFn: async () => {
      const { ip_geo_point } = ipLocation || {
        ip_geo_point: {
          lat: 0,
          lng: 0
        }
      }

      const { communities, spots } = await fetchExplores({
        user_coordinate: {
          lat: ip_geo_point.lat.toString(),
          lng: ip_geo_point.lng.toString()
        },
        language: locale,
        user_id: session.user_id,
        size: SIZE
      })
      return { communities, spots }
    }
  })

  useScrollingCallback({
    callback: showPopup,
    scrollDistance: POPUP_TRIGGER_HEIGHT.board,
    enabled: device !== 'desktop' && !isOpen
  })

  return (
    <div className='w-full mb:mt-[64px]'>
      <div className='p-[10px] border-b border-glass_special-separators'>
        <h2 className='text-header text-label-l1 mb-[10px] flex justify-between'>
          {t('search.trending_communities')}
          <Link
            href={`/search/trendingCommunity`}
            className=' text-callout text-primary-p1 cursor-pointer'
          >
            {t('search.see_more')}
          </Link>
        </h2>

        <CommunitiesList
          communities={data?.communities || []}
          loading={isLoading}
        />
      </div>
      <div className=' p-[10px] '>
        <h2 className='my-[10px] text-header text-label-l1 mb-[10px] flex justify-between'>
          {t('search.video_spotlights')}
        </h2>

        <VideoSpotlights spots={data?.spots || []} loading={isLoading} />
      </div>
    </div>
  )
}

function withRefreshingId(Component: typeof SearchExplores) {
  const WithRefreshingId = () => {
    const refreshingId = useRefreshSearchPageStore(state => state.refreshingId)

    return <Component key={refreshingId} />
  }

  return WithRefreshingId
}

export default withRefreshingId(SearchExplores)
